import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { is_phone_number_a_mobile, phone_number } from 'src/app/tools/phone-number';
import { TemplateService } from '../../services/template.service';
import { ActivatedRoute, Router } from '@angular/router';
import { KioskStateService, KioskState } from 'src/app/services/kiosk-state.service';
import { Subject, debounceTime, interval, takeUntil } from 'rxjs';
import { TaskService } from 'src/app/services/task.service';
import { Task } from '@contracts/interfaces/task.interface';

@Component({
  selector: 'ap-kios-mode-dialog',
  templateUrl: 'kios-mode.dialog.html',
  styleUrls: ['./kios-mode.dialog.scss']
})
export class KiosModeDialog implements OnInit, OnDestroy {

    public panel = 'home';
    private lastPanel: string | null = null;
    public consoleTypes: string[] = ['PlayStation 5', 'PlayStation 4', 'Xbox Series X|S', 'Xbox One', 'Nintendo Switch', 'Nintendo Switch Lite', 'Steam Deck', 'Other'];
    public selectedConsoleType: string | null = null;
    public consoleIssue = '';
    public laptopModelNumber = '';
    public laptopIssue = '';
    public dataRecoveryTypes: string[] = ['External hard drive', 'USB flash drive', 'Internal hard drive', 'SSD', 'Memory card', 'Phone', 'Other'];
    public selectedDataRecoveryType: string | null = null;
    public dataRecoveryFiles = '';
    public otherIssue = '';
    public newCustomer = { name: '', phone: '', email: '' };
    public existingSearch = '';
    public activeTaskCount = 0;

    private saveTrigger$ = new Subject<void>();
    private destroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<KiosModeDialog>,
        public dialog: MatDialog,
        private route: ActivatedRoute,
        private router: Router,
        private kioskStateService: KioskStateService,
        private taskService: TaskService
    ) {
        this.saveTrigger$
          .pipe(debounceTime(300), takeUntil(this.destroy$))
          .subscribe(() => this.persistState());
    }

    ngOnInit(): void {
        const initialPanel = this.route.snapshot.queryParamMap.get('panel');
        if (initialPanel && this.isValidPanel(initialPanel)) {
            this.panel = initialPanel;
        }

        this.loadState();
        this.startPolling();
        this.loadActiveTasks();
        this.startTaskPolling();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    switchPanel(panel: string) {
        this.lastPanel = this.panel;
        this.panel = panel;
        this.syncPanelToUrl();
        this.scheduleSave();
    }

    openConsoleForm() {
        this.switchPanel('console');
    }

    openLaptopForm() {
        this.switchPanel('laptop');
    }

    openDataRecoveryForm() {
        this.switchPanel('data-recovery');
    }

    openOtherForm() {
        this.switchPanel('other');
    }

    goBack() {
        if (this.panel === 'pick-job' && this.lastPanel && this.lastPanel !== 'home') {
            this.panel = this.lastPanel;
        } else {
            this.panel = 'home';
        }
        this.lastPanel = null;
        this.syncPanelToUrl();
        this.scheduleSave();
    }

    private syncPanelToUrl() {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { panel: this.panel === 'home' ? null : this.panel },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private isValidPanel(panel: string): boolean {
        return ['home', 'new-customer', 'existing-customer', 'pick-job', 'console', 'laptop', 'data-recovery', 'other'].includes(panel);
    }

    scheduleSave() {
        this.saveTrigger$.next();
    }

    private persistState() {
        const state: KioskState = {
            panel: this.panel,
            newCustomer: this.newCustomer,
            existingSearch: this.existingSearch,
            console: {
                type: this.selectedConsoleType,
                issue: this.consoleIssue
            },
            laptop: {
                modelNumber: this.laptopModelNumber,
                issue: this.laptopIssue
            },
            dataRecovery: {
                type: this.selectedDataRecoveryType,
                files: this.dataRecoveryFiles
            },
            other: {
                issue: this.otherIssue
            }
        };

        this.kioskStateService.saveState(state).subscribe({
            error: (err) => console.error('Failed to save kiosk state', err)
        });
    }

    private startPolling() {
        interval(5000).pipe(takeUntil(this.destroy$)).subscribe(() => this.loadState(true));
    }

    private startTaskPolling() {
        interval(5000)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.loadActiveTasks());
    }

    private loadActiveTasks() {
        this.taskService.getActive().subscribe({
            next: (tasks: Task[]) => {
                this.activeTaskCount = tasks?.length ?? 0;
            },
            error: (err) => console.error('Failed to load active tasks', err)
        });
    }

    private loadState(fromPoll: boolean = false) {
        this.kioskStateService.getState().subscribe({
            next: (state) => {
                if (!state) {
                    return;
                }
                if (state.panel && this.isValidPanel(state.panel) && (fromPoll ? state.panel !== this.panel : true)) {
                    this.panel = state.panel;
                    this.syncPanelToUrl();
                }
                if (state.newCustomer) {
                    this.newCustomer = { ...this.newCustomer, ...state.newCustomer };
                }
                if (state.existingSearch) {
                    this.existingSearch = state.existingSearch;
                }
                if (state.console) {
                    this.selectedConsoleType = state.console.type ?? null;
                    this.consoleIssue = state.console.issue ?? '';
                }
                if (state.laptop) {
                    this.laptopModelNumber = state.laptop.modelNumber ?? '';
                    this.laptopIssue = state.laptop.issue ?? '';
                }
                if (state.dataRecovery) {
                    this.selectedDataRecoveryType = state.dataRecovery.type ?? null;
                    this.dataRecoveryFiles = state.dataRecovery.files ?? '';
                }
                if (state.other) {
                    this.otherIssue = state.other.issue ?? '';
                }
            },
            error: (err) => console.error('Failed to load kiosk state', err)
        });
    }

    formatTaskLabel(task: Task | undefined | null): string {
        if (!task) return 'Task';
        if (task.subject) return task.subject;
        if (task.type) return task.type.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return 'Task';
    }
}
